import React, { useState, useEffect } from 'react';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { supabase } from '@/lib/supabase';
import { motion } from 'framer-motion';
import { format } from 'date-fns';
import {
  FileText,
  MapPin,
  Calendar,
  User,
  Building2,
  Users,
  Clock,
  CheckCircle,
  AlertCircle,
  Send,
  Edit,
  XCircle,
  PoundSterling,
  Award,
  Loader2
} from 'lucide-react';

/**
 * BookingHeader - Compact header showing key booking information
 * Shows: Course name, status, and key stats (delegates, cost, provider, dates)
 * Includes auto-save status and submit button for draft status
 */
const BookingHeader = ({
  booking,
  bookedOption,
  trainingProvider,
  delegateCount,
  saving = false,
  lastSaved = null,
  onSubmit,
  submitting = false,
  canSubmit = true
}) => {
  const [quoteOptionCount, setQuoteOptionCount] = useState(0);

  useEffect(() => {
    if (booking?.id && booking?.status === 'live') {
      fetchQuoteOptionCount();
    }
  }, [booking?.id, booking?.status]);

  const fetchQuoteOptionCount = async () => {
    const { count, error } = await supabase
      .from('quote_options')
      .select('*', { count: 'exact', head: true })
      .eq('training_quote_id', booking.id);
    if (!error) setQuoteOptionCount(count || 0);
  };

  const formatDate = (dateString) => {
    if (!dateString) return null;
    return new Date(dateString).toLocaleDateString('en-GB', {
      day: 'numeric',
      month: 'short',
      year: 'numeric'
    });
  };

  const getStatusConfig = () => {
    const isCompleted = booking?.completed_date !== null;

    switch (booking?.status) {
      case 'draft':
        return {
          label: 'Draft',
          bgColor: 'bg-gray-100',
          textColor: 'text-gray-700',
          borderColor: 'border-gray-200',
          icon: Edit
        };
      case 'live':
        return {
          label: 'Live Quote',
          bgColor: 'bg-blue-100',
          textColor: 'text-blue-700',
          borderColor: 'border-blue-200',
          icon: Send
        };
      case 'booked':
        return {
          label: isCompleted ? 'Completed' : 'Booked',
          bgColor: isCompleted ? 'bg-purple-100' : 'bg-green-100',
          textColor: isCompleted ? 'text-purple-700' : 'text-green-700',
          borderColor: isCompleted ? 'border-purple-200' : 'border-green-200',
          icon: isCompleted ? Award : CheckCircle
        };
      default:
        return {
          label: booking?.status || 'Unknown',
          bgColor: 'bg-gray-100',
          textColor: 'text-gray-700',
          borderColor: 'border-gray-200',
          icon: FileText
        };
    }
  };

  const statusConfig = getStatusConfig();
  const StatusIcon = statusConfig.icon;

  // Calculate days until expiry for live quotes
  const calculateDaysUntilExpiry = () => {
    if (!booking?.expiry_date) return null;
    const days = Math.floor((new Date(booking.expiry_date) - new Date()) / (1000 * 60 * 60 * 24));
    return days;
  };

  const daysUntilExpiry = calculateDaysUntilExpiry();
  const isExpiringSoon = daysUntilExpiry !== null && daysUntilExpiry <= 7 && daysUntilExpiry >= 0;
  const isExpired = daysUntilExpiry !== null && daysUntilExpiry < 0;

  // Get requester info
  const requesterName = booking?.user_profiles?.display_name ||
    (booking?.user_profiles?.first_name ? `${booking.user_profiles.first_name} ${booking.user_profiles.last_name || ''}`.trim() : null);

  return (
    <motion.div
      className="bg-white rounded-lg shadow-lg border border-gray-200"
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.4, ease: [0.22, 1, 0.36, 1] }}
    >
      <div className="p-6">
        <div className="flex items-start gap-5">
          {/* Course Icon */}
          <div className="h-16 w-16 rounded-lg bg-gradient-to-br from-indigo-500 to-purple-600 flex items-center justify-center flex-shrink-0">
            <FileText className="h-8 w-8 text-white" />
          </div>

          <div className="flex-1 min-w-0">
            {/* Course Name and Status */}
            <div className="flex items-start justify-between gap-4">
              <div className="flex-1 min-w-0">
                <h1 className="text-xl font-bold text-gray-900 truncate">
                  {booking?.course_name || 'Training Course'}
                </h1>

                <div className="flex flex-wrap items-center gap-2 mt-2">
                  {/* Status Badge */}
                  <Badge className={`${statusConfig.bgColor} ${statusConfig.textColor} ${statusConfig.borderColor}`}>
                    <StatusIcon className="h-3 w-3 mr-1" />
                    {statusConfig.label}
                  </Badge>

                  {/* Reference Badge */}
                  {booking?.external_quote_id && (
                    <Badge variant="outline" className="bg-white text-gray-600">
                      #{booking.external_quote_id}
                    </Badge>
                  )}

                  {/* Expiry Warning for Live Quotes */}
                  {booking?.status === 'live' && isExpired && (
                    <Badge className="bg-red-100 text-red-800 border-red-200">
                      <XCircle className="h-3 w-3 mr-1" />
                      Expired
                    </Badge>
                  )}
                  {booking?.status === 'live' && isExpiringSoon && !isExpired && (
                    <Badge className="bg-yellow-100 text-yellow-800 border-yellow-200">
                      <AlertCircle className="h-3 w-3 mr-1" />
                      Expires in {daysUntilExpiry} day{daysUntilExpiry !== 1 ? 's' : ''}
                    </Badge>
                  )}
                </div>
              </div>

              {/* Top Right - Auto-save status and Submit button */}
              <div className="flex items-center gap-3 flex-shrink-0">
                {/* Auto-save indicator (all statuses) */}
                <div className="flex items-center gap-2 text-sm text-gray-500">
                  {saving ? (
                    <>
                      <Loader2 className="h-4 w-4 animate-spin" />
                      <span>Saving...</span>
                    </>
                  ) : lastSaved ? (
                    <>
                      <CheckCircle className="h-4 w-4 text-green-500" />
                      <span>Saved {format(lastSaved, 'HH:mm')}</span>
                    </>
                  ) : null}
                </div>

                {/* Send Request button (draft only) */}
                {booking?.status === 'draft' && onSubmit && (
                  <Button
                    onClick={onSubmit}
                    disabled={submitting || !canSubmit}
                  >
                    {submitting ? (
                      <>
                        <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                        Sending...
                      </>
                    ) : (
                      <>
                        <Send className="h-4 w-4 mr-2" />
                        Send Request
                      </>
                    )}
                  </Button>
                )}
              </div>
            </div>

            {/* Key Stats Row */}
            <div className="flex flex-wrap items-center gap-3 mt-4">
              {/* Delegates */}
              <div className="flex items-center gap-1.5 px-2.5 py-1.5 bg-purple-50 rounded-md border border-purple-200">
                <Users className="h-3.5 w-3.5 text-purple-600" />
                <span className="text-xs font-medium text-purple-700">
                  {delegateCount || booking?.delegates || 0} delegates
                </span>
              </div>

              {/* Quote Options (for live) */}
              {booking?.status === 'live' && (
                <div className="flex items-center gap-1.5 px-2.5 py-1.5 bg-blue-50 rounded-md border border-blue-200">
                  <FileText className="h-3.5 w-3.5 text-blue-600" />
                  <span className="text-xs font-medium text-blue-700">
                    {quoteOptionCount} options
                  </span>
                </div>
              )}

              {/* Cost (for booked) */}
              {bookedOption?.quote_value && (
                <div className="flex items-center gap-1.5 px-2.5 py-1.5 bg-green-50 rounded-md border border-green-200">
                  <PoundSterling className="h-3.5 w-3.5 text-green-600" />
                  <span className="text-xs font-medium text-green-700">
                    £{bookedOption.quote_value.toLocaleString()}
                  </span>
                </div>
              )}

              {/* Provider (for booked) */}
              {trainingProvider && (
                <div className="flex items-center gap-1.5 px-2.5 py-1.5 bg-gray-50 rounded-md border border-gray-200">
                  <Building2 className="h-3.5 w-3.5 text-gray-500" />
                  <span className="text-xs font-medium text-gray-700">
                    {trainingProvider.trainer_name}
                  </span>
                </div>
              )}

              {/* Training Date (for booked) */}
              {booking?.status === 'booked' && booking?.requested_date && (
                <div className="flex items-center gap-1.5 px-2.5 py-1.5 bg-indigo-50 rounded-md border border-indigo-200">
                  <Calendar className="h-3.5 w-3.5 text-indigo-600" />
                  <span className="text-xs font-medium text-indigo-700">
                    {formatDate(booking.requested_date)}
                  </span>
                </div>
              )}

              {/* Location - only show if onsite or classroom delivery */}
              {booking?.locations &&
               booking?.delivery_methods?.some(m => ['onsite', 'classroom'].includes(m)) && (
                <div className="flex items-center gap-1.5 px-2.5 py-1.5 bg-gray-50 rounded-md border border-gray-200">
                  <MapPin className="h-3.5 w-3.5 text-gray-500" />
                  <span className="text-xs font-medium text-gray-700 truncate max-w-[150px]">
                    {booking.locations}
                  </span>
                </div>
              )}

              {/* Requester */}
              {requesterName && (
                <div className="flex items-center gap-1.5 px-2.5 py-1.5 bg-gray-50 rounded-md border border-gray-200">
                  <User className="h-3.5 w-3.5 text-gray-500" />
                  <span className="text-xs font-medium text-gray-700">
                    {requesterName}
                  </span>
                </div>
              )}

              {/* Project */}
              {booking?.projects?.name && (
                <div className="flex items-center gap-1.5 px-2.5 py-1.5 bg-gray-50 rounded-md border border-gray-200">
                  <FileText className="h-3.5 w-3.5 text-gray-500" />
                  <span className="text-xs font-medium text-gray-700 truncate max-w-[120px]">
                    {booking.projects.name}
                  </span>
                </div>
              )}

              {/* Completed Date */}
              {booking?.completed_date && (
                <div className="flex items-center gap-1.5 px-2.5 py-1.5 bg-purple-50 rounded-md border border-purple-200">
                  <CheckCircle className="h-3.5 w-3.5 text-purple-600" />
                  <span className="text-xs font-medium text-purple-700">
                    Completed {formatDate(booking.completed_date)}
                  </span>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </motion.div>
  );
};

export default BookingHeader;
